/**
 * Path utilities for handling subdomain-based routing
 *
 * In production:
 * - Landing page: stocksentiment.io
 * - App: app.stocksentiment.io
 * - API: stocksentiment.io/api
 *
 * In development:
 * - localhost:3000 behaves like app subdomain (for testing app functionality)
 */

/**
 * Check if we're on the app subdomain or localhost (for development)
 * In production: true for app.stocksentiment.io
 * In development: true for localhost (to test app functionality)
 */
export const isAppSubdomain = (): boolean => {
  const hostname = window.location.hostname;
  return hostname.startsWith('app.') || hostname === 'localhost';
};

/**
 * Get the base path for app routes
 * On app subdomain: returns ''
 * On main domain: returns '/app'
 */
export const getAppBasePath = (): string => {
  return isAppSubdomain() ? '' : '/app';
};

/**
 * Get full app path for navigation
 * @param path - The path suffix (e.g., '/settings', '/scanner')
 * @returns Full path for React Router navigation
 */
export const getAppPath = (path: string = ''): string => {
  const basePath = getAppBasePath();
  if (!path || path === '/') {
    return basePath || '/';
  }
  return basePath + path;
};

/**
 * Get full app URL for external links (cross-domain navigation)
 * Use this for links from landing page to app
 * @param path - The path suffix
 * @returns Full URL including protocol and domain
 */
export const getAppUrl = (path: string = ''): string => {
  const hostname = window.location.hostname;

  // On production main domain, redirect to app subdomain
  if (hostname === 'stocksentiment.io' || hostname === 'www.stocksentiment.io') {
    return `https://app.stocksentiment.io${path}`;
  }

  // On app subdomain or localhost, use relative path
  if (hostname.startsWith('app.') || hostname === 'localhost') {
    return path || '/';
  }

  // Fallback: use /app prefix
  return `/app${path}`;
};

/**
 * Get the settings path with optional hash
 * @param hash - Optional hash (e.g., 'subscription', 'alerts')
 * @returns Settings path
 */
export const getSettingsPath = (hash?: string): string => {
  const path = getAppPath('/settings');
  return hash ? `${path}#${hash}` : path;
};

/**
 * Get the upgrade path (settings with upgrade param)
 */
export const getUpgradePath = (): string => {
  return getAppPath('/settings?upgrade=true');
};
